"use client"

import Image from "next/image"
import Link from "next/link"

import { Button } from "@/components/ui/button"
import { SketchFill } from "@/components/ui/sketch-frame/fill"
import { sketchTones } from "@/components/ui/sketch-frame/tones"
import { routes } from "@/constants/routes"
import type { VerificationBannerDTO } from "@/domain/home/verification-banner/contracts"
import { userUIStore } from "@/stores/userUIStore"

import verificationShield from "./images/shield.webp"
import { verificationBannerTexts as t } from "./localization/texts"
import { VerificationBannerEmailButton } from "./VerificationBannerEmailButton"

const CTA_CLASS = "text-base px-3.5 xl:px-5 xl:text-lg"

type VerificationBannerProps = {
  banner: VerificationBannerDTO
}

export function VerificationBanner({ banner }: VerificationBannerProps) {
  const { toggleDepositModal } = userUIStore()
  const copy = copyForBanner(banner, toggleDepositModal)

  return (
    <section aria-label={t.ariaLabel} className="xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      <div className="relative isolate flex w-full min-h-[94px] max-w-full items-center overflow-hidden rounded-[10px] gap-3 p-4 sm:gap-4 xl:h-full xl:gap-4 xl:px-5 xl:py-4">
        <SketchFill
          skin={sketchTones.stone}
          className="inset-0 rounded-[10px]"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 z-1 rounded-[10px] ring-1 ring-inset ring-white/10"
        />
        <Image
          src={verificationShield}
          alt=""
          aria-hidden
          className="pointer-events-none absolute left-0 top-1/2 z-1 h-[94px] w-auto -translate-y-1/2 xl:h-[120px]"
        />
        <div className="relative z-10 flex min-w-0 flex-1 flex-col justify-center gap-2.5 xl:gap-3">
          <h2 className="text-xs font-semibold leading-tight xl:text-lg">
            {copy.title}
            {copy.reward ? (
              <>
                {" "}
                <span className="text-success-400">{copy.reward}</span>
              </>
            ) : null}
          </h2>
          {copy.description ? (
            <p className="text-[10px] font-normal xl:text-sm xl:leading-snug">
              {copy.description}
            </p>
          ) : null}
        </div>
        {copy.cta ? (
          <div className="relative z-10 flex shrink-0 flex-col items-center gap-1">
            {copy.cta}
            {copy.hint ? (
              <p className="text-[10px] leading-none text-white/50 font-normal xl:text-xs">
                {copy.hint}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  )
}

function copyForBanner(
  banner: VerificationBannerDTO,
  openDeposit: () => void
) {
  switch (banner.kind) {
    case "first-deposit":
      return {
        title: t.firstDeposit.title,
        reward: null,
        description: null,
        hint: null,
        cta: (
          <Button
            type="button"
            variant="hand"
            className={CTA_CLASS}
            onClick={openDeposit}
          >
            {t.firstDeposit.button}
          </Button>
        )
      }
    case "email":
      return {
        title: t.email.title,
        reward: null,
        description: null,
        hint: null,
        cta: <VerificationBannerEmailButton className={CTA_CLASS} />
      }
    case "withdrawals": {
      const country = banner.countryName || t.withdrawals.fallbackCountry
      return {
        title: t.withdrawals.title.replace("{country}", country),
        reward: null,
        description: null,
        hint: null,
        cta: null
      }
    }
    default:
      return {
        title: t.kyc.title,
        reward: t.kyc.reward,
        description: t.kyc.description,
        hint: t.kyc.hint,
        cta: (
          <Button asChild variant="hand" className={CTA_CLASS}>
            <Link href={routes.VERIFY}>{t.kyc.button}</Link>
          </Button>
        )
      }
  }
}
